import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, Clock, AlertCircle, ChevronLeft, Receipt } from "lucide-react";
import { useI18n } from "@/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND}/api/public/shop` });

const STATUS_UI = {
  pending: { icon: Clock, cls: "text-amber-600 bg-amber-50 border-amber-200" },
  paid: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  failed: { icon: XCircle, cls: "text-red-600 bg-red-50 border-red-200" },
  cancelled: { icon: XCircle, cls: "text-slate-500 bg-slate-50 border-slate-200" },
};

export default function ShopOrderConfirmation() {
  const { slug, payToken } = useParams();
  const { t } = useI18n();
  const [ctx, setCtx] = useState(null);
  const [busy, setBusy] = useState(true);
  const [acting, setActing] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await shopApi.get(`/pay/${payToken}`); setCtx(data); }
    catch { setNotFound(true); }
    finally { setBusy(false); }
  }, [payToken]);
  useEffect(() => { load(); }, [load]);

  const finalize = async (outcome) => {
    setActing(true);
    try { await shopApi.post(`/pay/${payToken}`, { outcome }); await load(); }
    catch { /* refetch shows current state */ }
    finally { setActing(false); }
  };

  if (busy) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  if (notFound || !ctx) return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center" data-testid="sf-conf-notfound">
      <AlertCircle className="h-12 w-12 text-slate-300" />
      <p className="text-slate-600">{t("shop.storefront.conf_not_found")}</p>
      <Link to={`/shop/${slug}`} className="rounded-xl bg-slate-900 px-6 py-2.5 font-semibold text-white">{t("shop.storefront.conf_back")}</Link>
    </div>
  );

  const st = STATUS_UI[ctx.payment_status] || STATUS_UI.pending;
  const StatusIcon = st.icon;
  const showSandbox = ctx.method === "test_sandbox" && ctx.payment_status === "pending" && !ctx.finalized && !ctx.expired;
  const showManual = ctx.method === "manual_invoice";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sf-conf">
      <div className="mx-auto max-w-lg px-4 py-8">
        <Link to={`/shop/${slug}`} className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800"><ChevronLeft className="h-4 w-4" />{t("shop.storefront.conf_back")}</Link>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <Receipt className="h-7 w-7 text-slate-700" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">{t("shop.storefront.conf_title")}</h1>
              <p className="text-sm text-slate-500">{t("shop.storefront.conf_thanks")}</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
            <div className="text-sm text-slate-500">{t("shop.storefront.order_number")}: <b className="text-slate-800" data-testid="sf-conf-order-number">{ctx.order_number}</b></div>
            <div className="text-base font-bold text-slate-900" data-testid="sf-conf-total">€ {ctx.total}</div>
          </div>

          <div className={`mt-4 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold ${st.cls}`} data-testid="sf-conf-status" role="status" aria-live="polite">
            <StatusIcon className="h-5 w-5" />
            <span>{t("shop.storefront.conf_status")}: {t(`shop.storefront.pstatus_${ctx.payment_status}`)}</span>
          </div>

          {ctx.items?.length > 0 && (
            <div className="mt-5">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{t("shop.storefront.conf_items")}</h2>
              <ul className="space-y-1.5">
                {ctx.items.map((it, i) => (
                  <li key={i} className="flex justify-between text-sm text-slate-600" data-testid={`sf-conf-item-${i}`}>
                    <span className="min-w-0 truncate pr-2">{it.qty} × {it.name}</span>
                    <span className="shrink-0">€ {it.unit_price_net}</span>
                  </li>
                ))}
                {ctx.shipping && (
                  <li className="flex justify-between text-sm text-slate-500">
                    <span>{t("shop.storefront.conf_shipping")}: {ctx.shipping.name}</span>
                    <span>€ {ctx.shipping.price_net}</span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {showManual && ctx.instructions && (
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="sf-conf-instructions">
              <h2 className="mb-1 text-sm font-semibold text-slate-700">{t("shop.storefront.manual_instructions_title")}</h2>
              <p className="whitespace-pre-line text-sm text-slate-600">{ctx.instructions}</p>
            </div>
          )}
          {showManual && !ctx.instructions && (
            <p className="mt-4 text-sm text-slate-500" data-testid="sf-conf-manual-note">{t("shop.storefront.manual_payment_note")}</p>
          )}

          {showSandbox && (
            <div className="mt-5" data-testid="sf-conf-sandbox">
              <p className="mb-2 text-sm text-slate-500">{t("shop.storefront.sandbox_choose")}</p>
              <div className="grid gap-2 sm:grid-cols-3">
                <button disabled={acting} onClick={() => finalize("success")} data-testid="sf-pay-success"
                  className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50">
                  {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{t("shop.storefront.pay_success")}
                </button>
                <button disabled={acting} onClick={() => finalize("failure")} data-testid="sf-pay-failure"
                  className="flex items-center justify-center gap-1.5 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50">
                  <XCircle className="h-4 w-4" />{t("shop.storefront.pay_failure")}
                </button>
                <button disabled={acting} onClick={() => finalize("cancel")} data-testid="sf-pay-cancel"
                  className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-50">
                  {t("shop.storefront.pay_cancel")}
                </button>
              </div>
            </div>
          )}

          {ctx.finalized && ctx.method === "test_sandbox" && (
            <p className="mt-4 text-xs text-slate-400" data-testid="sf-conf-finalized">{t("shop.storefront.finalized_note")}</p>
          )}
          {ctx.expired && ctx.payment_status === "pending" && (
            <p className="mt-4 text-xs text-amber-600" data-testid="sf-conf-expired">{t("shop.storefront.expired_note")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
